import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowLeft, Play, FileText, CheckCircle, ExternalLink, Clock, BookOpen, GraduationCap, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CourseDetailPage() {
  const { id } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submissionUrl, setSubmissionUrl] = useState('');
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isCompleted = Array.isArray(progress) && progress.some(p => p.course_id === id && p.completed);

  const fetchCourseData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [courseRes, progressRes] = await Promise.all([
        axios.get(`${API_URL}/api/courses/${id}`, { headers }),
        axios.get(`${API_URL}/api/progress`, { headers })
      ]);
      setCourse(courseRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [id, getAuthHeaders]);

  useEffect(() => {
    fetchCourseData();
  }, [fetchCourseData]);

  const toggleComplete = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/progress`, {
        course_id: id,
        completed: !isCompleted
      }, { headers });
      
      const progressRes = await axios.get(`${API_URL}/api/progress`, { headers });
      setProgress(progressRes.data);
      toast.success(isCompleted ? 'Course marked as incomplete' : 'Course marked as complete!');
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const handleSubmitAssignment = async (e) => {
    e.preventDefault();
    if (!submissionUrl.trim()) {
      toast.error('Please provide a submission URL');
      return;
    }

    setSubmitting(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/assignments`, {
        course_id: id,
        submission_url: submissionUrl,
        notes: submissionNotes
      }, { headers });
      
      toast.success('Assignment submitted successfully!');
      setSubmissionUrl('');
      setSubmissionNotes('');
    } catch (error) {
      toast.error('Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen pt-14 lg:pt-0">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background">
          <div className="animate-pulse">Loading course...</div>
        </main>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 p-8 bg-background">
          <p>Course not found</p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen pt-14 lg:pt-0" data-testid="course-detail-page">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background overflow-auto">
        {/* Back Link */}
        <Link 
          to="/courses" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors"
          data-testid="back-to-courses"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Courses</span>
        </Link>

        {/* Visitor Banner */}
        {user?.role === 'visitor' && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-sm" data-testid="visitor-banner">
            <p className="text-amber-800 text-sm font-medium">You are viewing this page as a visitor. Lesson content is not available in visitor mode.</p>
          </div>
        )}

        {/* Course Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="px-3 py-1 bg-secondary text-sm font-medium rounded">
                  {course.is_capstone ? 'Capstone Project' : `Course ${course.order}`}
                </span>
                <span className="px-3 py-1 bg-muted text-sm text-muted-foreground rounded flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {course.duration}
                </span>
                {isCompleted && (
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Completed
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-primary mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {course.title}
              </h1>
              <p className="text-muted-foreground max-w-2xl">
                {course.description}
              </p>
            </div>
            {user?.role !== 'admin' && user?.role !== 'visitor' && (
              <Button
                variant={isCompleted ? "outline" : "default"}
                onClick={toggleComplete}
                className="flex items-center gap-2"
                data-testid="mark-complete-button"
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-primary border-primary text-primary-foreground' : 'border-current'}`}>
                  {isCompleted && <CheckCircle className="w-3 h-3" />}
                </span>
                {isCompleted ? 'Mark Incomplete' : 'Mark as Complete'}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content - Sections */}
          <div className="lg:col-span-2 space-y-6">
            {/* Course Sections */}
            {course.sections && course.sections.length > 0 ? (
              <Card className="border border-border shadow-none" data-testid="course-sections">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-medium flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    <BookOpen className="w-5 h-5" />
                    Course Sections ({course.sections.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <Accordion type="single" collapsible className="w-full">
                    {course.sections.map((section, index) => (
                      <AccordionItem value={`section-${index}`} key={section.title || `section-${index}`} data-testid={`section-${index}`}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3 text-left">
                            <span className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium flex-shrink-0">
                              {index + 1}
                            </span>
                            <span className="font-medium">{section.title}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pl-11 space-y-4">
                            {/* Video Section */}
                            <div className="border border-border rounded-sm overflow-hidden">
                              {section.video_embed_url ? (
                                <div className="aspect-video w-full">
                                  <iframe
                                    src={section.video_embed_url}
                                    title={section.title}
                                    className="w-full h-full border-0"
                                    allowFullScreen
                                    allow="autoplay"
                                  />
                                </div>
                              ) : (
                                <div className="aspect-video bg-muted flex flex-col items-center justify-center text-muted-foreground">
                                  <Play className="w-10 h-10 mb-2" />
                                  <p className="text-sm">Video coming soon</p>
                                </div>
                              )}
                            </div>

                            {/* PDF Section */}
                            <div className="border border-border rounded-sm overflow-hidden">
                              {section.pdf_url ? (
                                <div className="space-y-0">
                                  <div className="aspect-[4/5] sm:aspect-[4/5] w-full">
                                    <iframe
                                      src={section.pdf_url}
                                      title={`${section.title} - PDF`}
                                      className="w-full h-full border-0"
                                      allowFullScreen
                                    />
                                  </div>
                                  <div className="flex items-center justify-between p-3 bg-muted/30 border-t border-border">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 bg-red-50 rounded flex items-center justify-center">
                                        <FileText className="w-4 h-4 text-red-600" />
                                      </div>
                                      <span className="text-sm font-medium">Course Materials (PDF)</span>
                                    </div>
                                    {section.pdf_download_url && (
                                      <a
                                        href={section.pdf_download_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 text-sm font-medium"
                                        data-testid={`download-pdf-${index}`}
                                      >
                                        <Download className="w-3.5 h-3.5" />
                                        Download PDF
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ) : section.pdf_download_url ? (
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-4 bg-muted/30">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-red-50 rounded flex items-center justify-center">
                                      <FileText className="w-4 h-4 text-red-600" />
                                    </div>
                                    <span className="text-sm font-medium">Course Materials (PDF)</span>
                                  </div>
                                  <a
                                    href={section.pdf_download_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 text-sm font-medium"
                                    data-testid={`download-pdf-${index}`}
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                    Download PDF
                                  </a>
                                </div>
                              ) : (
                                <div className="flex items-center gap-4 p-4 bg-muted/30">
                                  <div className="w-16 h-16 bg-muted rounded-sm flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-8 h-8 text-muted-foreground" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-muted-foreground">PDF Materials</p>
                                    <p className="text-sm text-muted-foreground">Coming soon</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            ) : (
              <Card className="border border-border shadow-none">
                <CardContent className="p-8 text-center text-muted-foreground">
                  {course.is_capstone ? (
                    <div className="max-w-lg mx-auto space-y-4">
                      <GraduationCap className="w-14 h-14 mx-auto text-primary/60" />
                      <h3 className="text-lg font-medium text-primary" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        {course.program === 'doctor' ? 'Doctoral Dissertation' : 'Capstone Project'}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {course.program === 'doctor'
                          ? 'After the successful completion of all eight courses and once you have been scored for each, our academic team will contact you with the doctoral dissertation requirements and guidelines.'
                          : course.program === 'mdppm'
                          ? 'After the successful completion of all five courses and once you have been scored for each, our academic team will contact you with the capstone project requirements and guidelines.'
                          : 'After the successful completion of all nine courses and once you have been scored for each, our academic team will contact you with the capstone project requirements and guidelines.'}
                      </p>
                      <p className="text-sm text-muted-foreground font-medium">
                        {course.program === 'doctor'
                          ? 'Please complete all eight courses successfully before proceeding to the doctoral dissertation.'
                          : course.program === 'mdppm'
                          ? 'Please complete all five courses successfully before proceeding to the capstone project.'
                          : 'Please complete all nine courses successfully before proceeding to the capstone project.'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No sections available for this course</p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Assignment submission moved to dedicated Assignments page */}
          </div>

          {/* Sidebar - Course Info */}
          <div className="space-y-6">
            {/* Course Image */}
            {course.image_url && (
              <Card className="border border-border shadow-none overflow-hidden">
                <img
                  src={course.image_url}
                  alt={course.title}
                  className="w-full h-48 object-cover"
                />
              </Card>
            )}

            {/* Course Summary */}
            <Card className="border border-border shadow-none" data-testid="course-summary">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Course Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Duration</span>
                  <span className="text-sm font-medium">{course.duration}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Sections</span>
                  <span className="text-sm font-medium">{course.sections?.length || 0}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="text-sm text-muted-foreground">Assignment</span>
                  <span className="text-sm font-medium">1 per course</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">Status</span>
                  {isCompleted ? (
                    <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Completed
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-amber-600">In Progress</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
